import { useMemo } from 'react';

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import CustomCheckbox from '@/components/Checkbox';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import useUserDetails from '@/hooks/useUserDetails';
import { getMiniTableSymbols } from '@/pages/Markets/MarketsComponents/MiniTablesContainer/utils/getMiniTableSymbols';
import type { MiniTableBodyRow, MiniTableType } from '@/pages/Markets/MarketsComponents/types';
import formatNumberInBraces from '@/utils/formatNumberInBraces';
import { isValidDateString } from '@/utils/isValidDateString';

const MiniTable = ({ table }: { table: MiniTableType }) => {
  const { formatDateStringInBraces } = useDateText();

  const { addSymbolsToSelection, areAllSymbolsSelected, removeSymbolsFromSelection, isSpotLocked } =
    useSelectionFunctions();

  const symbols = useMemo(() => getMiniTableSymbols(table), [table]);

  const isSymbolsSelected = useMemo(() => {
    return areAllSymbolsSelected(symbols);
  }, [symbols, areAllSymbolsSelected]);

  const { thead, tbody } = useMemo(() => {
    if ('symbol' in table.rows[0]) {
      return { thead: null, tbody: table.rows as MiniTableBodyRow[] };
    }
    return {
      thead: table.rows[0],
      tbody: table.rows.slice(1) as MiniTableBodyRow[]
    };
  }, [table]);

  const { isUserExpired } = useUserDetails();
  const factsheetCodes = useMemo(() => {
    return symbols.map(({ Code }) => Code).join(',');
  }, [symbols]);
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pl: '27px',
          pr: '16px',
          height: '26px'
        }}
      >
        <CustomCheckbox
          checked={isSymbolsSelected}
          disabled={isUserExpired}
          modeLocked={isSpotLocked}
          onLockedClick={() => {
            addSymbolsToSelection(symbols);
          }}
          sx={{ mb: '3px' }}
          onChange={() => {
            if (isSymbolsSelected) {
              removeSymbolsFromSelection(symbols);
            } else {
              addSymbolsToSelection(symbols);
            }
          }}
        />
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            flex: 1,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
            overflow: 'hidden'
          }}
        >
          {table.name}
        </Typography>

        <FactsheetDialogButton code={factsheetCodes} />
      </Box>
      <Table
        stickyHeader
        aria-label='commodities table'
        size='small'
        sx={{
          borderCollapse: 'collapse'
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              borderBottom: '1px solid',
              borderColor: 'white'
            }}
          >
            {thead?.cells.map((cell, i) => (
              <TableCell
                key={`thead-cell-${cell}-${i}`}
                align={i > 0 ? 'right' : 'left'}
                sx={{
                  pl: '27px',
                  pr: '16px',
                  backgroundColor: '#121836'
                }}
              >
                {cell}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            '& .markets-table-row:nth-of-type(even)': {
              backgroundColor: 'secondary.main'
            }
          }}
        >
          {tbody.map((row, rowIndex) => {
            const isHighlighted = row.layout?.highlight === 'true';
            let backgroundColor = 'transparent';

            if (isHighlighted) {
              backgroundColor = '#282F48';
            } else if (rowIndex % 2 === 0) {
              backgroundColor = '#121836';
            }
            const rowKey = row.cells[0] || `row-${rowIndex}`;

            return (
              <TableRow
                key={`body-${rowKey}`}
                className='markets-table-row'
                sx={{ backgroundColor }}
              >
                {row.cells.map((cell, cellIndex) => {
                  const isFirstCell = cellIndex === 0;
                  const trimmedCell = cell.slice(1, -1);
                  const isDate =
                    Number.isNaN(Number(trimmedCell)) && isValidDateString(trimmedCell);
                  const colSpan = cell === '' && row.cells.length === 1 ? 3 : 1;

                  let content = '';
                  if (cell !== '') {
                    content = isDate
                      ? formatDateStringInBraces(cell, DateTextVariants.MONTH_YEAR)
                      : formatNumberInBraces(cell);
                  }

                  return (
                    <TableCell
                      key={`${rowIndex}-${cellIndex}`}
                      align={isFirstCell ? 'left' : 'right'}
                      colSpan={colSpan}
                      sx={{
                        pl: isFirstCell ? '27px' : 0,
                        pr: '16px',
                        width: isFirstCell ? '40%' : '30%'
                      }}
                    >
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};
export default MiniTable;
